/* eslint-disable camelcase */

import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

import { HvButton } from "@hitachivantara/uikit-react-core";
import {
  HvTableColumnConfig,
  useHvTable,
  HvTableContainer,
  HvTable,
  HvTableHead,
  HvTableRow,
  HvTableHeader,
  HvTableBody,
  HvTableCell,
  useHvTableSticky,
  HvCellProps,
} from "@hitachivantara/uikit-react-lab";
import { Level0Good, Open, Success } from "@hitachivantara/uikit-react-icons";
import { useTheme } from "@material-ui/core";
import moment from "moment";

import { IncidentTransaction, useTimeSettings } from "lib/api";
import { getUserDisplay } from "lib/utils/user";

import { actionsColumn, textColumn } from "components/common/SimpleTable";
import { OverflowTooltip } from "components/common";
import LevelBadge from "components/common/LevelBadge";
import { formatInTimezone } from "lib/utils/datetime";
import { nodeFallback, stringFallback } from "lib/utils/fallbacks";

import { useExpandContentStyles } from "./styles";

interface SopStepDetailsProps {
  sopId: string;
  sopSteps: SopStep[];
  sopStepsDetails?: IncidentTransaction[];
  openSopStep: (sopId: string, stepIndex: number) => void;
}

interface SopStepWithDetails extends SopStep {
  assignee?: string;
  status: SopStatus;
  duration?: string;
  comment?: string;
}

const SopStepDetails: React.FC<SopStepDetailsProps> = ({
  sopId,
  sopSteps,
  sopStepsDetails,
  openSopStep,
}) => {
  const { t } = useTranslation("map");
  const classes = useExpandContentStyles();
  const theme = useTheme();

  const columns = useMemo(
    (): HvTableColumnConfig<SopStepWithDetails>[] => [
      {
        Header: t("sections.sop.columns.steps") as string,
        accessor: "name",
        style: { width: "25%", position: "relative" },
        Cell: ({
          value,
          row: {
            original: { status, order = 0 },
          },
        }: HvCellProps<SopStepWithDetails, string>) => {
          if (status === "Completed") {
            return (
              <span className={classes.stepDetailsName}>
                <Level0Good
                  semantic="sema1"
                  style={{ float: "left", width: 32, height: 32 }}
                  height={22}
                  width={22}
                />
                <OverflowTooltip data={stringFallback(value)} />
              </span>
            );
          }

          if (status === "InProgress") {
            return (
              <span className={classes.stepDetailsName}>
                <Success
                  color="acce2"
                  style={{ float: "left", width: 32, height: 32 }}
                  height={22}
                  width={22}
                />
                <OverflowTooltip data={stringFallback(value)} />
              </span>
            );
          }

          return (
            <LevelBadge
              style={{ width: "100%", top: 4 }}
              absolutePosition
              name={value}
              color={
                status !== "Locked"
                  ? theme.hv.palette.accent.acce1
                  : theme.hv.palette.atmosphere.atmo5
              }
              textIcon={`${(order + 1).toString()}`}
            />
          );
        },
      },
      textColumn({
        Header: t("sections.sop.columns.completedDate") as string,
        accessor: "completedDate",
        style: { width: 160 },
      }),
      textColumn({
        Header: t("sections.sop.columns.assignee") as string,
        accessor: "assignee",
        style: { width: "15%" },
      }),
      textColumn({
        Header: t("sections.sop.columns.duration") as string,
        accessor: "duration",
        style: { width: 120 },
      }),
      textColumn({
        Header: t("sections.sop.columns.instructions") as string,
        accessor: "instructions",
        style: { width: "30%" },
      }),
      textColumn({
        Header: t("sections.sop.columns.comment") as string,
        accessor: "comment",
        style: { width: "30%" },
      }),
      actionsColumn({
        id: "edit",
        style: { padding: 0 },
        Cell: ({ row }: HvCellProps<SopStepWithDetails>) => {
          return (
            <HvButton
              aria-label={t("labels.editCategoryButton")}
              icon
              onClick={() => openSopStep(sopId, row.index)}
            >
              <Open />
            </HvButton>
          );
        },
      }),
    ],
    [
      classes.stepDetailsName,
      openSopStep,
      sopId,
      t,
      theme.hv.palette.accent.acce1,
      theme.hv.palette.atmosphere.atmo5,
    ]
  );
  const { timezone } = useTimeSettings();
  const data = useMemo((): SopStepWithDetails[] => {
    let foundInProgress = false;
    return sopSteps.map<SopStepWithDetails>((step) => {
      const stepDetails = sopStepsDetails?.find(
        (detail) => step.id === detail.fk_sop_step_id
      );

      if (stepDetails) {
        const {
          inct_assigned_to,
          inct_start_date_time,
          inct_completed_date_time,
          inct_status,
          inct_value,
        } = stepDetails;

        if (inct_status === "InProgress") {
          foundInProgress = true;
        }

        return {
          ...step,
          transaction: stepDetails,
          assignee: stringFallback(getUserDisplay(inct_assigned_to)),
          completedDate:
            inct_status === "Completed"
              ? formatInTimezone(inct_completed_date_time, timezone)
              : "—",
          status: inct_status,
          duration:
            inct_status === "Completed"
              ? moment(inct_start_date_time).from(
                  moment(inct_completed_date_time),
                  true
                )
              : undefined,
          comment: inct_value,
        };
      }

      if (!foundInProgress) {
        foundInProgress = true;
        return { ...step, status: "Unlocked" };
      }

      return { ...step, status: "Locked" };
    });
  }, [sopSteps, sopStepsDetails, timezone]);

  const instance = useHvTable<SopStepWithDetails>(
    {
      columns,
      data,
      stickyHeader: true,
      getRowId: useCallback((v: SopStepWithDetails): string => `${v.id}`, []),
      initialState: {},
      disableSortBy: true,
    },
    useHvTableSticky
  );

  const {
    getTableProps,
    getTableHeadProps,
    getTableBodyProps,
    prepareRow,
    headers,
    rows,
  } = instance;

  return (
    <HvTableRow>
      <HvTableCell className={classes.expandContentRoot} colSpan={5}>
        <HvTableContainer className={classes.tableContainer}>
          <HvTable {...getTableProps()} style={{ tableLayout: "fixed" }}>
            <HvTableHead {...getTableHeadProps()}>
              <HvTableRow className={classes.tableHeaderRow}>
                {headers.map((col) => (
                  <HvTableHeader
                    {...col.getHeaderProps()}
                    className={classes.tableHeader}
                  >
                    <OverflowTooltip
                      data={nodeFallback(col.render("Header"))}
                      paragraphOverflow
                    />
                  </HvTableHeader>
                ))}
              </HvTableRow>
            </HvTableHead>
            <HvTableBody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);

                const { key, ...rowProps } = row.getRowProps();

                return (
                  <React.Fragment key={key}>
                    <HvTableRow
                      {...rowProps}
                      className={clsx({
                        [classes.stepLocked]: row.original.status === "Locked",
                      })}
                      style={{ height: 40 }}
                    >
                      {row.cells.map((cell) => (
                        <HvTableCell
                          className={classes.tableCell}
                          {...cell.getCellProps()}
                        >
                          {cell.render("Cell")}
                        </HvTableCell>
                      ))}
                    </HvTableRow>
                  </React.Fragment>
                );
              })}
            </HvTableBody>
          </HvTable>
        </HvTableContainer>
      </HvTableCell>
    </HvTableRow>
  );
};

export default SopStepDetails;
